import { isSensitiveMessage, WebsocketMessage } from '@playcastdotio/Messaging';

/**
 * MessageRouter - Routes external messages to registered handlers
 *
 * This is a simple routing layer that delivers messages from external sources
 * (websockets, IPC) to the appropriate coordinator handlers.
 *
 * Key responsibilities:
 * - Register handlers for specific message types
 * - Route incoming messages to all registered handlers
 * - Handle errors gracefully
 * - Log warnings for unhandled message types
 *
 * This is NOT an event emitter - it's a "post office" that delivers messages.
 * The coordinators that receive messages are responsible for:
 * - Parsing/validating the message data
 * - Updating their internal state
 * - Emitting semantic events for other coordinators/UI to consume
 *
 * Pattern:
 * External Message → MessageRouter → Coordinator Handler → State Update → Event Emission
 *
 * Usage:
 * ```typescript
 * // In coordinator:
 * coordinator.registerMessageHandlers(router: IMessageRouter): void {
 *   router.register(SomeMessageType.SOME_MESSAGE, this.onSomeMessage.bind(this));
 * }
 *
 * // In external handler (e.g., websocket):
 * router.route(message);
 * ```
 */

// ========================================
// MessageRouter Interface
// ========================================

/**
 * IMessageRouter - Interface for message routing
 *
 * Accepts messages with a `type` field and routes them to registered handlers.
 */
export interface IMessageRouter {
  /**
   * Register a handler for a specific message type
   *
   * @param messageType - The message type string to handle
   * @param handler - The function to call when this message type is received
   */
  register(
    messageType: string,
    handler: (message: WebsocketMessage) => void,
  ): void;

  /**
   * Route an incoming message to all registered handlers for its type
   *
   * @param message - The message to route (uses message.type to find handlers)
   */
  route(message: WebsocketMessage): void;
}

// ========================================
// MessageRouter Implementation
// ========================================

/**
 * MessageRouter - Simple implementation of IMessageRouter
 *
 * Routes messages based on their `type` field to registered handlers.
 * Handlers receive the full message object and can narrow the type themselves.
 */
export class MessageRouter implements IMessageRouter {
  private handlers: Map<string, Array<(message: WebsocketMessage) => void>> = new Map();

  /**
   * Register a handler for a specific message type
   */
  register(
    messageType: string,
    handler: (message: WebsocketMessage) => void,
  ): void {
    const key = String(messageType);
    if (!this.handlers.has(key)) {
      this.handlers.set(key, []);
    }
    this.handlers.get(key)!.push(handler);
    console.log(`[MessageRouter] Registered handler for: ${key}`);
  }

  /**
   * Route an incoming message to all registered handlers for its type
   */
  route(message: WebsocketMessage): void {
    const handlers = this.handlers.get(message.type);
    if (!handlers || handlers.length === 0) {
      console.warn(`[MessageRouter] No handlers registered for: ${message.type}`);
      return;
    }

    handlers.forEach((handler) => {
      try {
        handler(message);
      } catch (error) {
        if (isSensitiveMessage(message)) console.error(`[MessageRouter] Private handler failed for ${message.type}`);
        else console.error(`[MessageRouter] Handler error for ${message.type}:`, error);
      }
    });
  }
}

// ========================================
// Global Singleton Instances
// ========================================

/**
 * realtimeMessageRouter - Singleton for routing realtime/websocket messages
 *
 * Use this to register handlers in coordinators that need to respond to websocket events.
 */
export const realtimeMessageRouter = new MessageRouter();

/**
 * playjectorMessageRouter - Singleton for routing Playjector/IPC messages
 *
 * Use this to register handlers in coordinators that need to respond to Playjector events.
 * TODO: Update to accept PlaycastMessage<T> types when implementing PlayjectorCoordinator
 */
export const playjectorMessageRouter = new MessageRouter();
